import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  Intervention,
  InterventionScopeNameEnum,
  Task,
} from 'graphql-common';
import { useTableItemsBulkSelect } from '@lib/hooks/useTableItemsBulkSelect';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import Trans from '@lib/components/Trans/Trans';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import { materialSymbolsOutlined } from '@lib/enums/common';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import Search from '@lib/components/Search/Search';
import Table from '@lib/components/Table/Table';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import useSearchParam from '@lib/hooks/useSearchParam';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import useActions from 'routes/Interventions/utils/useActions';
import { useLocation, useParams } from 'react-router-dom';
import useModalManagement from 'routes/Interventions/utils/useModalManagement';
import getTableActionsMenu from 'routes/Interventions/utils/getTableActionsMenu';
import getTableActionsForSelected from 'routes/Interventions/utils/getTableActionsForSelected';
import ViewOnlyChip from '@lib/components/ViewOnlyChip/ViewOnlyChip';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import { IconName } from '@lib/components/Modal/enums';
import getTaskCompletionSelectOption from 'routes/Intervention/utils/getTaskCompletionSelectOption';
import getTableColumns from './utils/getInterventionsTableColumns';

type Props = {
  // From TaskTabsPage's `useReporting` — the same `dashboardTaskShort` query
  // that feeds the sidebar, so this tab doesn't fetch the task a second time
  // and stays in sync after a sidebar status change.
  taskData?: Task;
  taskLoading?: boolean;
  // Refetches that task after an intervention is closed, deleted or has its
  // status changed here — the backend solves/reopens the checks it covers,
  // which changes the task's status and `canInterventionCreate`.
  onInterventionsChanged?: () => void;
};

export default function TaskInterventionsContainer({
  taskData: task,
  taskLoading,
  onInterventionsChanged,
}: Props) {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const [searchQuery] = useSearchParam();

  const { getUserAccessByComponent }: ContextProps = useOutletContext();
  const isViewOnly =
    getUserAccessByComponent(AccessComponentEnum.InterventionsManagement) ===
    AccessLevelEnum.Read;

  const useBulkSelectResult = useTableItemsBulkSelect<Intervention>();
  const useModalManagementResult = useModalManagement({ useBulkSelectResult });

  const name = task?.name || '';
  const groupKey = task?.groupKey || '';
  const canInterventionCreate = task?.canInterventionCreate?.value;
  // `message` is a generic "not authorized" — the actual reason ("Task must be
  // completed.", "already covered by an intervention", …) is in `reasons`.
  const canInterventionCreateMessage =
    task?.canInterventionCreate?.reasons?.fullMessages?.join(' ') ||
    task?.canInterventionCreate?.message;

  const useInterventionActionsResult = useActions({
    taskId: groupKey,
    useModalManagementResult,
    useBulkSelectResult,
    searchQuery,
    pathname,
    search,
    skipInterventionsFetch: !task?.id,
    interventionScope: InterventionScopeNameEnum.All,
    onInterventionsChanged,
  });

  const {
    interventionsQueryHook,
    onCloseModal,
    onConfirmedInterventionDestroy,
    openCreateInterventionPage,
    onConfirmedInterventionClose,
  } = useInterventionActionsResult;

  const { totalCount } = interventionsQueryHook;
  const bulkSelectedItemsCount = totalCount
    ? useBulkSelectResult.getSelectedItemsCount(totalCount)
    : 0;

  const tableColumns = getTableColumns({
    onView: useInterventionActionsResult.openViewInterventionPage,
    onStatusChangeHandler: useInterventionActionsResult.onStatusChangeHandler,
    t,
    isViewOnly,
  });
  const tableActionsMenu = getTableActionsMenu({
    onView: useInterventionActionsResult.openViewInterventionPage,
    onEdit: useInterventionActionsResult.openEditInterventionPage,
    onDelete: useInterventionActionsResult.openInterventionDestroyModal,
    onClose: useInterventionActionsResult.openInterventionCloseModal,
    isViewOnly,
    t,
  });
  const tableActionsForSelected = getTableActionsForSelected({
    onBulkClose: useModalManagementResult.openInterventionCloseModal,
    selectedItemsCount: useBulkSelectResult.getSelectedItemsCount(totalCount),
    isViewOnly,
    t,
  });

  const onCreateAssetIntervention = () => {
    openCreateInterventionPage({
      assetId: task?.assetId,
      siteAreaId: task?.siteAreaId,
      siteId: task?.siteId,
      taskId: id,
      // Link the task's own check: an intervention covering another
      // occurrence's check leaves this task Unsolved and open to a new one.
      taskCompletions: task?.completion
        ? [getTaskCompletionSelectOption(task.completion)]
        : undefined,
    });
  };

  const renderCreateButton = (key: string) => (
    <Tooltip
      key={key}
      body={canInterventionCreateMessage}
      disabled={!!canInterventionCreate || !canInterventionCreateMessage}
    >
      <Button
        buttonText={t('create-intervention')}
        buttonType={ButtonTypes.primaryFilled}
        leftIcon="add"
        onClick={onCreateAssetIntervention}
        leftIconClassName={materialSymbolsOutlined}
        disabled={!canInterventionCreate}
      />
    </Tooltip>
  );

  const emptyState = (
    <ListEmptyState
      title={t('no-interventions-yet')}
      text={<Trans i18nKey="no-interventions-text" />}
      actions={isViewOnly ? [] : [renderCreateButton('create')]}
    />
  );

  if (interventionsQueryHook.error) {
    return (
      <ErrorPage
        status={getStatusFromError(interventionsQueryHook.error)}
        description={getDescriptionFromError(interventionsQueryHook.error)}
      />
    );
  }

  return (
    <>
      <BreadCrumbs
        data={getBreadCrumbsDataByKey({ pathname, t, entity: task })}
      />
      {(taskLoading && !task) || interventionsQueryHook.firstLoading ? (
        <CircleLoader />
      ) : (
        <WhiteBox>
          <DashboardTitleHeader
            title={name}
            actions={
              isViewOnly ? (
                <ViewOnlyChip key="view" />
              ) : (
                renderCreateButton('add')
              )
            }
          />
          {(!!interventionsQueryHook.collection?.length || searchQuery) && (
            <Search placeholder={t('asset-interventions-search-placeholder')} />
          )}
          <Table
            actionsForSelected={tableActionsForSelected}
            actionsMenu={tableActionsMenu}
            columns={tableColumns}
            data={interventionsQueryHook.collection}
            emptyState={emptyState}
            id="interventions"
            loading={interventionsQueryHook.loading}
            totalEntries={interventionsQueryHook.totalCount}
            useBulkSelectResult={useBulkSelectResult}
          />
        </WhiteBox>
      )}
      <ConfirmationModal
        confirmButtonLabel={t('yes-delete')}
        description={<Trans i18nKey="asset-documentation-delete-text" />}
        icon={IconName.Delete}
        isOpen={useModalManagementResult.isInterventionDestroyModalOpened}
        onClose={onCloseModal}
        onConfirm={onConfirmedInterventionDestroy}
        title={t('are-you-sure')}
      />
      <ConfirmationModal
        confirmButtonLabel={t('yes-close')}
        description={
          <Trans
            i18nKey={
              bulkSelectedItemsCount > 1
                ? 'interventions-close-text'
                : 'intervention-close-text'
            }
          />
        }
        icon={IconName.Warning}
        isOpen={useModalManagementResult.isInterventionCloseModalOpened}
        onClose={useModalManagementResult.closeAllModals}
        onConfirm={onConfirmedInterventionClose}
        title={t('are-you-sure')}
        useProgress
      />
    </>
  );
}
